import { Modal } from '@shared/designSystem/messages/Modal';
import { Organization } from '@models/Organization';
import { useOrganizationSelectorModalViewModel } from './OrganizationSelectorModalViewModel';
import { VerticalContainer } from '@shared/designSystem/Containers';
import SearchBar, { SearchBarRef } from '@shared/designSystem/SearchBar';
import { BoldText, Text } from '@shared/designSystem/Texts';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { SelectableList } from '@shared/designSystem/SelectableList';
import { useEffect, useRef } from 'react';

interface OrganizationSelectorModalScreenProps {
    isOpen: boolean;
    organizations: Organization[];
    selectedOrganization?: Organization;
    onClose: () => void;
    onOrganizationSelected: (organization: Organization) => void;
}

const OrganizationSelectorModalScreen: React.FC<OrganizationSelectorModalScreenProps> = ({
    isOpen,
    organizations,
    selectedOrganization,
    onClose,
    onOrganizationSelected
}) => {
    const viewModel = useOrganizationSelectorModalViewModel({
        organizations,
        selectedOrganization,
        onClose,
        onOrganizationSelected
    });
    const searchBarRef = useRef<SearchBarRef>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            searchBarRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Seleccionar organización"
            width="800px"
            height="600px"
            maxWidth="800px"
            maxHeight="600px"
        >
            <VerticalContainer height="100%" gap={designSystemStyles.gapSm}>
                <SearchBar
                    ref={searchBarRef}
                    placeholder={"Buscar por ID o nombre".localized()}
                    value={viewModel.searchQuery}
                    onChange={viewModel.setSearchQuery}
                />
                <SelectableList
                    items={viewModel.rows}
                    columns={[
                        {
                            id: 'id',
                            label: <Text text={"**ID**".localized()} markdown={true}/>,
                            width: '1fr',
                            renderCell: (organization: Organization) => <Text text={"**"+organization.id+"**"} markdown={true}/>
                        },
                        {
                            id: 'name',
                            label: <Text text={"**Nombre**".localized()} markdown={true}/>,
                            width: '2fr',
                            renderCell: (organization: Organization) => <Text text={organization.name} />
                        }
                    ]}
                    getItemKey={(organization) => organization.id}
                    onSelect={viewModel.selectOrganization}
                    isSelected={viewModel.isSelected}
                    emptyStateText={"No se encontraron organizaciones.".localized()}
                    minWidth="100%"
                />
            </VerticalContainer>
        </Modal>
    );
};

export default OrganizationSelectorModalScreen;
